// Cmd/Ctrl+K palette. P0 ships only the navigation surface — every
// admin page lands as a "Go to …" command. Subsequent phases plug in
// quick actions (freeze tenant by id, retry webhook, etc.) once those
// surfaces exist.
//
// Built on cmdk (the same primitive Linear / Vercel use). Wrapped in a
// Radix Dialog so the focus trap, escape-to-close, and click-outside
// behave like a real modal without our own implementation.

import { Command } from 'cmdk';
import {
  Activity,
  BarChart3,
  Database,
  GanttChart,
  type LucideIcon,
  ScrollText,
  ShieldAlert,
  Ticket,
  Users,
  UsersRound,
  Wrench,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog.js';
import { useStaffMe, type StaffScope } from '../../lib/providers.js';

interface PaletteCommand {
  id: string;
  label: string;
  hint?: string;
  to: string;
  icon: LucideIcon;
  // Subset of staff scopes that gate this command. Empty = always visible.
  requiredScopes: readonly StaffScope[];
}

const COMMANDS: PaletteCommand[] = [
  { id: 'overview', label: 'Go to Overview', to: '/', icon: BarChart3, requiredScopes: [] },
  {
    id: 'tenants',
    label: 'Go to Tenants',
    to: '/tenants',
    icon: Users,
    requiredScopes: ['staff:read', 'staff:support', 'staff:platform'],
  },
  {
    id: 'tickets',
    label: 'Go to Tickets',
    to: '/tickets',
    icon: Ticket,
    requiredScopes: ['staff:support', 'staff:platform'],
  },
  {
    id: 'audit',
    label: 'Go to Audit log',
    to: '/audit',
    icon: ScrollText,
    requiredScopes: [],
  },
  {
    id: 'billing',
    label: 'Go to Billing',
    to: '/billing',
    icon: GanttChart,
    requiredScopes: ['staff:finance', 'staff:platform'],
  },
  {
    id: 'money',
    label: 'Go to Money & Usage',
    to: '/money',
    icon: Activity,
    requiredScopes: ['staff:finance', 'staff:platform'],
  },
  {
    id: 'security',
    label: 'Go to Security signals',
    to: '/security',
    icon: ShieldAlert,
    requiredScopes: ['staff:support', 'staff:platform'],
  },
  {
    id: 'stripe',
    label: 'Go to Stripe Sync',
    to: '/stripe',
    icon: Database,
    requiredScopes: ['staff:finance', 'staff:platform'],
  },
  {
    id: 'health',
    label: 'Go to System health',
    to: '/health',
    icon: Wrench,
    requiredScopes: ['staff:platform'],
  },
  {
    id: 'staff',
    label: 'Go to Staff admin',
    to: '/staff',
    icon: UsersRound,
    requiredScopes: ['staff:platform'],
  },
];

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (to: string) => void;
}

export function CommandPalette({ open, onOpenChange, onNavigate }: CommandPaletteProps) {
  const me = useStaffMe();
  const have = new Set(me.scopes);
  const visible = COMMANDS.filter(
    (c) => c.requiredScopes.length === 0 || c.requiredScopes.some((s) => have.has(s)),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0">
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <Command label="Command palette" className="flex flex-col">
          <Command.Input
            placeholder="Search pages and actions…"
            className="border-b bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          <Command.List className="max-h-[400px] overflow-y-auto p-1">
            <Command.Empty className="px-4 py-6 text-center text-sm text-muted-foreground">
              No matching commands. Subsequent phases add tenant search, ticket
              jump-by-id, and quick actions here.
            </Command.Empty>
            <Command.Group heading="Navigation">
              {visible.map((cmd) => {
                const Icon = cmd.icon;
                return (
                  <Command.Item
                    key={cmd.id}
                    value={`${cmd.label} ${cmd.id}`}
                    onSelect={() => {
                      onNavigate(cmd.to);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm aria-selected:bg-accent aria-selected:text-foreground"
                  >
                    <Icon aria-hidden className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1">{cmd.label}</span>
                  </Command.Item>
                );
              })}
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
